import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import { ROLE_CONFIG, ROLE_DESCRIPTIONS, ACTIVE_DEALS, AUDIT_TRAIL } from './teamData';

function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG?.[role] || ROLE_CONFIG?.Viewer;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg?.color}`}>
      {role}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    Active: 'bg-green-100 text-green-700',
    Inactive: 'bg-red-100 text-red-700',
    Pending: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${map?.[status] || 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
}

function Avatar({ initials, role, size = 8 }) {
  const cfg = ROLE_CONFIG?.[role] || ROLE_CONFIG?.Viewer;
  return (
    <div
      className="flex items-center justify-center rounded-full text-white font-semibold flex-shrink-0"
      style={{ width: `${size * 4}px`, height: `${size * 4}px`, fontSize: size < 10 ? '0.75rem' : '1rem', background: cfg?.avatarBg }}
    >
      {initials}
    </div>
  );
}

function EditRoleModal({ member, onClose, onSave, showToast }) {
  const [selectedRole, setSelectedRole] = useState(member?.role);
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e?.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-foreground">Edit Role</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><Icon name="X" size={18} /></button>
        </div>
        <div className="flex items-center gap-3 mb-5 p-3 bg-muted rounded-xl">
          <Avatar initials={member?.initials} role={member?.role} size={9} />
          <div>
            <p className="font-caption font-semibold text-foreground">{member?.name}</p>
            <p className="font-caption text-sm text-muted-foreground">{member?.email}</p>
          </div>
        </div>
        <label className="block font-caption text-sm font-medium text-foreground mb-1">New Role</label>
        <select
          value={selectedRole}
          onChange={e => setSelectedRole(e?.target?.value)}
          className="w-full border border-border rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground"
        >
          {Object.keys(ROLE_CONFIG)?.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <p className="font-caption text-xs text-muted-foreground mb-4">{ROLE_DESCRIPTIONS?.[selectedRole]}</p>
        <label className="block font-caption text-sm font-medium text-foreground mb-1">Reason for change <span className="text-muted-foreground">(optional)</span></label>
        <textarea
          value={reason}
          onChange={e => setReason(e?.target?.value)}
          rows={3}
          placeholder="Briefly describe why this role is being changed…"
          className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground"
        />
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-border rounded-lg font-caption text-sm font-medium text-muted-foreground hover:bg-muted">Cancel</button>
          <button
            onClick={() => { onSave(member?.id, selectedRole); showToast(`Role updated for ${member?.name}`); onClose(); }}
            className="flex-1 px-4 py-2 rounded-lg font-caption text-sm font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
          >Save Changes</button>
        </div>
      </div>
    </div>
  );
}

function DeactivateModal({ member, onClose, onConfirm, showToast }) {
  const [reason, setReason] = useState('Departed organisation');
  const reasons = ['Departed organisation', 'Security concern', 'Temporary suspension', 'Other'];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e?.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Icon name="AlertTriangle" size={20} color="#d97706" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground">Deactivate Member</h3>
            <p className="font-caption text-sm text-muted-foreground">{member?.name}</p>
          </div>
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-muted"><Icon name="X" size={18} /></button>
        </div>
        <p className="font-caption text-sm text-foreground mb-4">Deactivating this member will revoke their access immediately. They will not be able to log in until reactivated.</p>
        <label className="block font-caption text-sm font-medium text-foreground mb-1">Reason</label>
        <select
          value={reason}
          onChange={e => setReason(e?.target?.value)}
          className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-red-400 bg-card text-foreground"
        >
          {reasons?.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-border rounded-lg font-caption text-sm font-medium text-muted-foreground hover:bg-muted">Cancel</button>
          <button
            onClick={() => { onConfirm(member?.id); showToast('Member deactivated — access revoked'); onClose(); }}
            className="flex-1 px-4 py-2 rounded-lg font-caption text-sm font-semibold text-white bg-red-600 hover:bg-red-700"
          >Confirm Deactivate</button>
        </div>
      </div>
    </div>
  );
}

function RemoveModal({ member, onClose, onConfirm, showToast }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e?.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Icon name="UserX" size={20} color="#dc2626" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground">Remove Member</h3>
            <p className="font-caption text-sm text-muted-foreground">{member?.name}</p>
          </div>
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-muted"><Icon name="X" size={18} /></button>
        </div>
        <p className="font-caption text-sm text-foreground mb-5">Are you sure you want to permanently remove <strong>{member?.name}</strong> from the platform? This action cannot be undone.</p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-border rounded-lg font-caption text-sm font-medium text-muted-foreground hover:bg-muted">Cancel</button>
          <button
            onClick={() => { onConfirm(member?.id); showToast(`${member?.name} removed from the platform`); onClose(); }}
            className="flex-1 px-4 py-2 rounded-lg font-caption text-sm font-semibold text-white bg-red-600 hover:bg-red-700"
          >Remove Member</button>
        </div>
      </div>
    </div>
  );
}

function MemberDrawer({ member, onClose, onSave, onDeactivate, isAdmin, showToast }) {
  const [drawerTab, setDrawerTab] = useState('profile');
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [selectedRole, setSelectedRole] = useState(member?.role);
  const [permissions, setPermissions] = useState({
    'View Deals': true, 'Create SPVs': false, 'Manage Documents': member?.role === 'Admin' || member?.role === 'Sponsor',
    'View Financials': member?.role !== 'Viewer', 'Manage Team': member?.role === 'Admin',
    'View ESG': true, 'Export Reports': member?.role !== 'Viewer', 'Access Data Room': true,
  });
  const [dealAccess, setDealAccess] = useState(ACTIVE_DEALS?.slice(0, member?.dealsAccess));

  const memberActivity = AUDIT_TRAIL?.filter(a => a?.actor === member?.name || a?.target === member?.name || a?.actor === member?.email)?.slice(0, 10);

  const togglePermission = (perm) => setPermissions(prev => ({ ...prev, [perm]: !prev?.[perm] }));
  const toggleDeal = (deal) => setDealAccess(prev => prev?.includes(deal) ? prev?.filter(d => d !== deal) : [...prev, deal]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-card shadow-2xl z-50 flex flex-col" style={{ animation: 'slideInRight 0.25s ease-out' }}>
        {/* Drawer Header */}
        <div className="flex items-center gap-4 p-5 border-b border-border">
          <Avatar initials={member?.initials} role={member?.role} size={12} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-foreground text-lg">{member?.name}</h3>
              <RoleBadge role={member?.role} />
              <StatusBadge status={member?.status} />
            </div>
            <p className="font-caption text-sm text-muted-foreground mt-0.5">Last active: {member?.lastActive}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted flex-shrink-0"><Icon name="X" size={18} /></button>
        </div>
        {/* Drawer Tabs */}
        <div className="flex border-b border-border px-5">
          {['profile', 'access', 'activity']?.map(t => (
            <button
              key={t}
              onClick={() => setDrawerTab(t)}
              className={`px-4 py-3 font-caption text-sm font-medium border-b-2 transition-colors ${
                drawerTab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'profile' ? 'Profile' : t === 'access' ? 'Access & Permissions' : 'Activity'}
            </button>
          ))}
        </div>
        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {drawerTab === 'profile' && (
            <div className="space-y-4">
              {[
                { label: 'Full Name', value: member?.name, icon: 'User' },
                { label: 'Email', value: member?.email, icon: 'Mail' },
                { label: 'Organisation', value: member?.organisation, icon: 'Building2' },
                { label: 'Job Title', value: member?.jobTitle, icon: 'Briefcase' },
                { label: 'Phone', value: member?.phone, icon: 'Phone' },
                { label: 'Date Joined', value: member?.dateJoined, icon: 'Calendar' },
                { label: 'Timezone', value: member?.timezone, icon: 'Globe' },
              ]?.map(({ label, value, icon }) => (
                <div key={label} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name={icon} size={15} color="var(--color-muted-foreground)" />
                  </div>
                  <div>
                    <p className="font-caption text-xs text-muted-foreground font-medium">{label}</p>
                    <p className="font-caption text-sm text-foreground font-medium">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {drawerTab === 'access' && (
            <div className="space-y-5">
              <div>
                <label className="block font-caption text-sm font-semibold text-foreground mb-2">Role</label>
                <select
                  value={selectedRole}
                  onChange={e => setSelectedRole(e?.target?.value)}
                  disabled={!isAdmin}
                  className="w-full border border-border rounded-lg px-3 py-2 font-caption text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground disabled:bg-muted disabled:text-muted-foreground"
                >
                  {Object.keys(ROLE_CONFIG)?.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="font-caption text-xs text-muted-foreground mt-1">{ROLE_DESCRIPTIONS?.[selectedRole]}</p>
              </div>
              <div>
                <p className="font-caption text-sm font-semibold text-foreground mb-3">Custom Permission Overrides</p>
                <div className="space-y-2">
                  {Object.entries(permissions)?.map(([perm, enabled]) => (
                    <div key={perm} className="flex items-center justify-between py-2 border-b border-border/50">
                      <span className="font-caption text-sm text-foreground">{perm}</span>
                      <button
                        onClick={() => isAdmin && togglePermission(perm)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-muted'} ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-caption text-sm font-semibold text-foreground mb-3">Deal Access ({dealAccess?.length} deals)</p>
                <div className="space-y-1.5">
                  {ACTIVE_DEALS?.map(deal => (
                    <div key={deal} className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-muted">
                      <span className="font-caption text-sm text-foreground">{deal}</span>
                      <button
                        onClick={() => isAdmin && toggleDeal(deal)}
                        className={`font-caption text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
                          dealAccess?.includes(deal) ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                        } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {dealAccess?.includes(deal) ? 'Access' : 'No Access'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {drawerTab === 'activity' && (
            <div className="space-y-3">
              {memberActivity?.length === 0 ? (
                <p className="font-caption text-sm text-muted-foreground text-center py-8">No recent activity found.</p>
              ) : memberActivity?.map(entry => (
                <div key={entry?.id} className="flex gap-3 p-3 rounded-xl bg-muted">
                  <div className="w-8 h-8 rounded-full bg-card border border-border flex items-center justify-center flex-shrink-0">
                    <Icon name={entry?.actionType === 'Login' ? 'LogIn' : entry?.actionType === 'Document Access' ? 'FileText' : 'Activity'} size={14} color="var(--color-muted-foreground)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-caption text-sm text-foreground">{entry?.description}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="font-caption text-xs text-muted-foreground">{entry?.timestamp?.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="font-caption text-xs text-muted-foreground">IP: {entry?.ip}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Drawer Footer */}
        {isAdmin && (
          <div className="border-t border-border p-4 flex gap-2">
            <button
              onClick={() => { onSave(member?.id, selectedRole); showToast('Member updated'); }}
              className="flex-1 px-4 py-2 rounded-lg font-caption text-sm font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
            >Save Changes</button>
            {member?.status !== 'Inactive' ? (
              <button onClick={() => setShowDeactivate(true)} className="px-4 py-2 rounded-lg font-caption text-sm font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200">Deactivate</button>
            ) : (
              <button onClick={() => { onDeactivate(member?.id, 'reactivate'); showToast('Member reactivated'); }} className="px-4 py-2 rounded-lg font-caption text-sm font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200">Reactivate</button>
            )}
            <button onClick={() => setShowRemove(true)} className="px-4 py-2 rounded-lg font-caption text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200">Remove</button>
          </div>
        )}
      </div>
      {showDeactivate && <DeactivateModal member={member} onClose={() => setShowDeactivate(false)} onConfirm={(id) => { onDeactivate(id, 'deactivate'); }} showToast={showToast} />}
      {showRemove && <RemoveModal member={member} onClose={() => setShowRemove(false)} onConfirm={(id) => { onDeactivate(id, 'remove'); onClose(); }} showToast={showToast} />}
    </>
  );
}

export default function MembersTab({ members, setMembers, isAdmin, showToast }) {
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [orgFilter, setOrgFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editRoleMember, setEditRoleMember] = useState(null);
  const [deactivateMember, setDeactivateMember] = useState(null);
  const PAGE_SIZE = 10;

  const orgs = useMemo(() => ['All', ...new Set(members.map(m => m.organisation))], [members]);

  const filtered = useMemo(() => {
    let list = [...members];
    if (roleFilter !== 'All') list = list?.filter(m => m?.role === roleFilter);
    if (statusFilter !== 'All') list = list?.filter(m => m?.status === statusFilter);
    if (orgFilter !== 'All') list = list?.filter(m => m?.organisation === orgFilter);
    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      list = list?.filter(m => m?.name?.toLowerCase()?.includes(q) || m?.email?.toLowerCase()?.includes(q) || m?.organisation?.toLowerCase()?.includes(q));
    }
    list?.sort((a, b) => {
      let av = a?.[sortField] || '', bv = b?.[sortField] || '';
      if (typeof av === 'string') av = av?.toLowerCase();
      if (typeof bv === 'string') bv = bv?.toLowerCase();
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return list;
  }, [members, roleFilter, statusFilter, orgFilter, searchQuery, sortField, sortDir]);

  const totalPages = Math.ceil(filtered?.length / PAGE_SIZE);
  const paginated = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const handleRoleSave = (id, newRole) => {
    setMembers(prev => prev?.map(m => m?.id === id ? { ...m, role: newRole } : m));
  };

  const handleDeactivate = (id, action) => {
    setMembers(prev => prev?.map(m => {
      if (m?.id !== id) return m;
      if (action === 'deactivate') return { ...m, status: 'Inactive' };
      if (action === 'reactivate') return { ...m, status: 'Active' };
      if (action === 'remove') return null;
      return m;
    })?.filter(Boolean));
  };

  const SortIcon = ({ field }) => (
    <Icon name={sortField === field ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={13} color="var(--color-muted-foreground)" />
  );

  return (
    <div>
      {/* Filter Bar */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="font-caption text-xs font-medium text-muted-foreground">Role</label>
          <select value={roleFilter} onChange={e => { setRoleFilter(e?.target?.value); setPage(1); }} className="border border-border rounded-lg px-2 py-1.5 font-caption text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground">
            {['All', 'Admin', 'Investor', 'Sponsor', 'Analyst', 'Viewer']?.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="font-caption text-xs font-medium text-muted-foreground">Status</label>
          <select value={statusFilter} onChange={e => { setStatusFilter(e?.target?.value); setPage(1); }} className="border border-border rounded-lg px-2 py-1.5 font-caption text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground">
            {['All', 'Active', 'Inactive', 'Pending']?.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="font-caption text-xs font-medium text-muted-foreground">Organisation</label>
          <select value={orgFilter} onChange={e => { setOrgFilter(e?.target?.value); setPage(1); }} className="border border-border rounded-lg px-2 py-1.5 font-caption text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground">
            {orgs?.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div className="ml-auto font-caption text-sm text-muted-foreground">{filtered?.length} member{filtered?.length !== 1 ? 's' : ''}</div>
      </div>
      {/* Table */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-muted border-b border-border">
                {[
                  { label: 'Member', field: 'name' },
                  { label: 'Role', field: 'role' },
                  { label: 'Organisation', field: 'organisation' },
                  { label: 'Status', field: 'status' },
                  { label: 'Last Active', field: 'lastActive' },
                  { label: 'Date Joined', field: 'dateJoined' },
                  { label: 'Deals', field: 'dealsAccess' },
                  { label: 'Actions', field: null },
                ]?.map(col => (
                  <th
                    key={col?.label}
                    className={`px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide ${col?.field ? 'cursor-pointer hover:text-foreground select-none' : ''}`}
                    onClick={() => col?.field && handleSort(col?.field)}
                  >
                    <div className="flex items-center gap-1">
                      {col?.label}
                      {col?.field && <SortIcon field={col?.field} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {paginated?.map(member => (
                <tr
                  key={member?.id}
                  className="hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => setSelectedMember(member)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar initials={member?.initials} role={member?.role} size={8} />
                      <div>
                        <p className="font-caption font-semibold text-foreground text-sm">{member?.name}</p>
                        <p className="font-caption text-xs text-muted-foreground">{member?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><RoleBadge role={member?.role} /></td>
                  <td className="px-4 py-3 font-caption text-sm text-muted-foreground">{member?.organisation}</td>
                  <td className="px-4 py-3"><StatusBadge status={member?.status} /></td>
                  <td className="px-4 py-3 font-caption text-sm text-muted-foreground">{member?.lastActive}</td>
                  <td className="px-4 py-3 font-caption text-sm text-muted-foreground">{member?.dateJoined}</td>
                  <td className="px-4 py-3 font-caption text-sm text-muted-foreground">{member?.dealsAccess} deals</td>
                  <td className="px-4 py-3" onClick={e => e?.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      {isAdmin && (
                        <button
                          onClick={() => setEditRoleMember(member)}
                          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground"
                          title="Edit Role"
                        >
                          <Icon name="Pencil" size={14} />
                        </button>
                      )}
                      {isAdmin && member?.status !== 'Inactive' && (
                        <button
                          onClick={() => setDeactivateMember(member)}
                          className="p-1.5 rounded-lg hover:bg-amber-50 text-muted-foreground hover:text-amber-600"
                          title="Deactivate"
                        >
                          <Icon name="UserMinus" size={14} />
                        </button>
                      )}
                      {isAdmin && member?.status === 'Inactive' && (
                        <button
                          onClick={() => { handleDeactivate(member?.id, 'reactivate'); showToast('Member reactivated'); }}
                          className="p-1.5 rounded-lg hover:bg-green-50 text-muted-foreground hover:text-green-600"
                          title="Reactivate"
                        >
                          <Icon name="UserCheck" size={14} />
                        </button>
                      )}
                      {member?.status === 'Pending' && (
                        <button
                          onClick={() => showToast(`Invite resent to ${member?.email}`)}
                          className="p-1.5 rounded-lg hover:bg-primary/8 text-muted-foreground hover:text-primary"
                          title="Resend Invite"
                        >
                          <Icon name="Send" size={14} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => { handleDeactivate(member?.id, 'remove'); showToast(`${member?.name} removed`); }}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600"
                          title="Remove"
                        >
                          <Icon name="Trash2" size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {paginated?.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center font-caption text-muted-foreground text-sm">No members match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="font-caption text-sm text-muted-foreground">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered?.length)} of {filtered?.length}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg font-caption text-sm border border-border disabled:opacity-40 hover:bg-muted">Previous</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)?.map(p => (
                <button key={p} onClick={() => setPage(p)} className={`px-3 py-1.5 rounded-lg font-caption text-sm border ${p === page ? 'border-primary bg-primary/8 text-primary font-semibold' : 'border-border hover:bg-muted'}`}>{p}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 rounded-lg font-caption text-sm border border-border disabled:opacity-40 hover:bg-muted">Next</button>
            </div>
          </div>
        )}
      </div>
      {/* Modals */}
      {selectedMember && (
        <MemberDrawer
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
          onSave={handleRoleSave}
          onDeactivate={handleDeactivate}
          isAdmin={isAdmin}
          showToast={showToast}
        />
      )}
      {editRoleMember && (
        <EditRoleModal
          member={editRoleMember}
          onClose={() => setEditRoleMember(null)}
          onSave={handleRoleSave}
          showToast={showToast}
        />
      )}
      {deactivateMember && (
        <DeactivateModal
          member={deactivateMember}
          onClose={() => setDeactivateMember(null)}
          onConfirm={(id) => handleDeactivate(id, 'deactivate')}
          showToast={showToast}
        />
      )}
    </div>
  );
}
